import React, { useMemo } from 'react';
import { MapPin, Smartphone, Monitor } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { getBrowserInfo, getGPSInstructions } from '../../services/locationService';
import './GpsInstructions.css';

/**
 * Composant affichant les instructions détaillées pour activer le GPS
 * S'adapte automatiquement selon l'OS et le navigateur de l'utilisateur
 *
 * @param {function} onRetry - Callback appelé quand l'utilisateur clique sur "Réessayer"
 */
const GpsInstructions = ({ onRetry }) => {
  const { t } = useTranslation();

  // Détecter l'OS et le navigateur
  const { os, browser } = useMemo(() => getBrowserInfo(), []);

  // Obtenir les instructions spécifiques
  const instructions = useMemo(() => getGPSInstructions(os, browser), [os, browser]);

  // Icône selon l'OS
  const DeviceIcon = useMemo(() => {
    return (os === 'android' || os === 'ios') ? Smartphone : Monitor;
  }, [os]);

  // Nom convivial du navigateur
  const browserName = useMemo(() => {
    const names = {
      chrome: 'Google Chrome',
      safari: 'Safari',
      firefox: 'Firefox',
      edge: 'Microsoft Edge',
      unknown: t('location.instructions.yourBrowser') || 'votre navigateur'
    };
    return names[browser] || names.unknown;
  }, [browser, t]);

  return (
    <div className="gps-instructions">
      <div className="gps-instructions-header">
        <div className="gps-instructions-icon">
          <MapPin size={48} className="text-primary" />
        </div>
        <h3 className="gps-instructions-title">{t('location.instructions.title')}</h3>
        <p className="gps-instructions-subtitle">
          {t('location.instructions.subtitle') ||
            `Instructions pour ${browserName} sur ${os.toUpperCase()}`}
        </p>
      </div>

      <div className="gps-instructions-content">
        {/* Afficher l'icône de l'appareil */}
        <div className="device-indicator">
          <DeviceIcon size={32} />
          <span className="device-name">
            {os === 'android' && 'Android'}
            {os === 'ios' && 'iOS'}
            {os === 'windows' && 'Windows'}
            {os === 'macos' && 'macOS'}
            {os === 'linux' && 'Linux'}
            {os === 'unknown' && 'Appareil inconnu'}
          </span>
        </div>

        {/* Liste des étapes */}
        <ol className="instructions-list">
          {instructions.map((step, index) => (
            <li key={index} className="instruction-step">
              <div className="step-number">{index + 1}</div>
              <div className="step-text">{step}</div>
            </li>
          ))}
        </ol>

        {/* Bouton réessayer */}
        <div className="instructions-actions">
          <button
            onClick={onRetry}
            className="btn btn-primary btn-lg btn-retry-gps"
            type="button"
          >
            <MapPin size={20} />
            <span>{t('location.instructions.retry') || 'Réessayer maintenant'}</span>
          </button>
        </div>

        {/* Note additionnelle */}
        <div className="instructions-note">
          <p>
            <strong>{t('location.instructions.noteTitle') || 'Note :'}</strong>{' '}
            {t('location.instructions.noteText') ||
              'Si le problème persiste, vérifiez que le GPS de votre appareil est bien activé dans les paramètres système.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default GpsInstructions;
